import { create } from 'zustand';
import { CopiedObject } from '../eventManager/types/eventTypes';

interface SelectedPoint {
  pointId: string;
  assemblyId: string;
  originalX: number;
  originalY: number;
}

interface GroupDragState {
  isDraggingGroup: boolean;
  dragStartPositions: Map<string, { x: number; y: number }>;
  dragOffset: { x: number; y: number };
  dragStartCursor: { x: number; y: number };
}

type CopyMode = 'extend' | 'create';

interface SelectionState {
  selectedPoints: SelectedPoint[];
  hasCopiedItems: boolean;
  copyMode: CopyMode;
  copiedObjects: CopiedObject[];
  groupDragState: GroupDragState;
  isFlipped: boolean;
  setSelectedPoints: (points: SelectedPoint[]) => void;
  setHasCopiedItems: (hasCopied: boolean) => void;
  setCopyMode: (mode: CopyMode) => void;
  setCopiedObjects: (objects: CopiedObject[]) => void;
  clearSelection: () => void;
  clearCopiedItems: () => void;
  startGroupDrag: (
    cursorPosition: { x: number; y: number },
    pointPositions: Map<string, { x: number; y: number }>
  ) => void;
  updateGroupDragOffset: (offset: { x: number; y: number }) => void;
  endGroupDrag: () => void;
  isPointInSelectedGroup: (pointId: string) => boolean;
  setIsFlipped: (flipped: boolean) => void;
  toggleFlip: () => void;
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  selectedPoints: [],
  hasCopiedItems: false,
  copyMode: 'create', // Default to creating new
  copiedObjects: [],
  groupDragState: {
    isDraggingGroup: false,
    dragStartPositions: new Map(),
    dragOffset: { x: 0, y: 0 },
    dragStartCursor: { x: 0, y: 0 },
  },
  isFlipped: false,
  setSelectedPoints: points => set({ selectedPoints: points }),
  setHasCopiedItems: hasCopied => set({ hasCopiedItems: hasCopied }),
  setCopyMode: mode => set({ copyMode: mode }),
  setCopiedObjects: objects => set({ copiedObjects: objects }),
  clearSelection: () => set({ selectedPoints: [], hasCopiedItems: false, isFlipped: false }),
  clearCopiedItems: () => set({ hasCopiedItems: false, copiedObjects: [] }),
  startGroupDrag: (cursorPosition, pointPositions) =>
    set({
      groupDragState: {
        isDraggingGroup: true,
        dragStartPositions: new Map(pointPositions),
        dragOffset: { x: 0, y: 0 },
        dragStartCursor: { ...cursorPosition },
      },
    }),
  updateGroupDragOffset: offset =>
    set(state => ({
      groupDragState: {
        ...state.groupDragState,
        dragOffset: offset,
      },
    })),
  endGroupDrag: () =>
    set(state => ({
      groupDragState: {
        ...state.groupDragState,
        isDraggingGroup: false,
        dragStartPositions: new Map(),
        dragOffset: { x: 0, y: 0 },
      },
    })),
  isPointInSelectedGroup: pointId => {
    const state = get();
    return state.selectedPoints.some(sp => sp.pointId === pointId);
  },
  setIsFlipped: flipped => set({ isFlipped: flipped }),
  toggleFlip: () => set(state => ({ isFlipped: !state.isFlipped })),
}));
